<script>
  import Chart from "svelte-chartjs/src/Bar.svelte";
  import { createEventDispatcher, onMount } from "svelte";
  import { generateCanvasGradient, makeWebsiteRequest, parseNumber, parsePercent, months, monthsShort } from "../../Common/tools";
  import UtmLinkGenerator from "../UtmLinkGenerator.svelte";
  import {External, M} from "../../external";

  export let filters = [],
    grouping = "daily",
    funnel = null,
    period = null,
    showReferers = true;

  const salesGradientColors = ["#FF55B8", "#FF8787"];
  const countsGradientColors = ["#3da6ff", "#257cc6"];
  let chartDataLabels = [];
  let monthLabels = [];
  let showMonthsBelow = false;
  let chartWrapper = null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: false,
      title: false,
      tooltip: {
        mode: "index",
        intersect: false,
        displayColors: false
      }
    },
    scales: {
      xAxes: {
        display: false
      },
      yAxisRevenue: {
        type: "linear",
        position: "left",
        display: false
      },
      yAxisCounts: {
        type: "linear",
        position: "right",
        display: false
      }
    },
    layout: {
      padding: 3
    }
  };

  let chartData = {
    labels: [],
    datasets: []
  };

  let chartValues = [];
  let stats = {};

  const dispatch = createEventDispatcher();

  export async function reload() {
    let from, to;
    if (period && period.from) {
      from = External.Tools.dateToISOStr(period.from);
    }
    if (period && period.to) {
      to = External.Tools.dateToISOStr(period.to);
    }
    if (!from) {
      let firstOrderTime = External.Website.firstOrderTime;
      from = External.Tools.dateToISOStr(new Date(firstOrderTime || External.createdTime * 1000));
    }
    if (!to) {
      to = External.Tools.dateToISOStr(new Date());
    }

    let response = await makeWebsiteRequest({
      action: "getOrderStats",
      method: "post",
      data: {
        type: "sales",
        grouping: grouping,
        funnel: funnel,
        from: from,
        to: to,
        filters: JSON.stringify(filters)
      }
    });

    if (!response.data) {
      return;
    }
    stats = response.data || {};

    let dataset = stats.groupedSales || {};

    chartDataLabels = Object.keys(dataset);
    monthLabels = [];
    showMonthsBelow = chartDataLabels.length > (chartWrapper.offsetWidth > 610 ? 14 : 7);
    chartDataLabels = chartDataLabels.map((label) => {
      let date;
      switch (grouping) {
        case "hourly":
          let hour = External.WebPlatform.parse(label);
          if (External.WebsiteSettings.getDateFormat() === "uk") {
            return hour;
          }
          if (hour === 0) {
            return "12";
          }
          if (hour > 12) {
            return hour - 12;
          }
          return hour;
        case "daily":
          date = new Date(label);
          if (showMonthsBelow) {
            let month = months[date.getMonth()];
            if (monthLabels.length === 0 || monthLabels[monthLabels.length - 1] !== month) {
              monthLabels.push(month);
            }
            return date.getDate();
          } else {
            return External.WebPlatform.dateToStrWithMonth(date, true, false, true, monthsShort);
          }
        case "monthly":
          date = new Date(label);
          return External.WebPlatform.dateToStrWithMonth(date, false, false, false, monthsShort);
        case "yearly":
          return label;
      }
      return label;
    });

    chartValues = Object.values(dataset);
    let orderCountsData = chartValues.map((el) => el.first);
    let orderTotalsData = chartValues.map((el) => el.second);

    let canvas = document.querySelector(".analytics-box-big canvas");
    let salesGradient = generateCanvasGradient(canvas, salesGradientColors);
    let countsGradient = generateCanvasGradient(canvas, countsGradientColors);

    chartData = {
      labels: chartDataLabels,
      datasets: [
        {
          label: "Sales",
          data: orderTotalsData,
          backgroundColor: salesGradient,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: true,
          borderRadius: 3,
          borderColor: "transparent",
          order: 1,
          yAxisID: "yAxisRevenue"
        },
        {
          label: "Orders",
          data: orderCountsData,
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: false,
          borderRadius: 3,
          borderColor: countsGradient,
          order: 0,
          type: "line",
          yAxisID: "yAxisCounts"
        }
      ]
    };
  }

  let showTopReferersBtnClick = () => {
    dispatch("showreferers");
  };

  let generateUtmLink = () => {
    External.Tools.showWindow({
      isModal: false,
      withDarkBackground: true,
      width: 520,
      buttons: [],
      noNav: true,
      noTitle: true,
      //markup: '<div class="popup-widget-wrapper bind-component"></div>',
      forceAtRight: External.showBuilderPopupsOnFullHeight,
      action: {
        onShow: (popup, ui) => {
          popup.bindComponent(
            new UtmLinkGenerator({
              target: popup.getContentArea(),
              props: {}
            })
          );
        }
      }
    });
  };

  let filterBySource = (source) => {
    dispatch("filtersources", {
      source: source
    });
  };

  onMount(() => {
    reload();
  });
</script>

<div class="col20 f-left" class:no-referers={!showReferers}>

   <!-- Top Analytics Part -->

  <div class="flex col20 f-left store-section-analytics-warapper three-columns flex-col-mobile" style="background: #f5f6fa; margin-top: 0;">
    <div class="analytics-box analytics-box-small">
      <h3 class="flex flex-row align-center">{M.SALES} <span class="chart-color ml-10" style="background: #f97f7f" /></h3>
      <h2 class="col20 f-left f-28 gray-text">
        <b>
          {External.Tools.currencyToStrWithSymbol(stats.sales)}
          {#if period && period.from}
            {#if stats.sales > stats.prevSales}
              <span class="increase">
                +{stats.prevOrders === 0 ? "100%" : parsePercent(stats.sales / stats.prevSales - 1)}
                <span class="material-icons none"> arrow_upward </span>
              </span>
            {:else if stats.sales < stats.prevSales}
              <span class="decrease">
                {stats.prevOrders === 0 ? "100%" : parsePercent(stats.sales / stats.prevSales - 1)}
                <span class="material-icons none"> arrow_downward </span>
              </span>
            {:else}
              <span class="no-change">
                <span class="material-icons none"> remove </span>
              </span>
            {/if}
          {/if}
        </b>
      </h2>
    </div>
    <div class="analytics-box analytics-box-small">
      <h3 class="flex flex-row align-center">{M.ORDERS} <span class="chart-color ml-10" style="background: #3da6ff" /></h3>
      <h2 class="col20 f-left f-28 gray-text">
        <b>
          {parseNumber(stats.orders)}
          {#if period && period.from}
            {#if stats.orders > stats.prevOrders}
              <span class="increase">
                +{stats.prevOrders === 0 ? "100%" : parsePercent(stats.orders / stats.prevOrders - 1)}
                <span class="material-icons none"> arrow_upward </span>
              </span>
            {:else if stats.orders < stats.prevOrders}
              <span class="decrease">
                {stats.prevOrders === 0 ? "-100%" : parsePercent(stats.orders / stats.prevOrders - 1)}
                <span class="material-icons none"> arrow_downward </span>
              </span>
            {:else}
              <span class="no-change">
                <span class="material-icons none"> remove </span>
              </span>
            {/if}
          {/if}
        </b>
      </h2>
    </div>

    <div class="analytics-box analytics-box-small">
      <h3>{M.AVERAGE_ORDER_VALUE}</h3>
      <h2 class="col20 f-left f-28 gray-text">
        <b>
          {External.Tools.currencyToStrWithSymbol(stats && stats.orders > 0 ? stats.sales / stats.orders : 0)}
        </b>
      </h2>
    </div>
    <div class="analytics-box analytics-box-small">
      <h3>{M.ABANDONED_CARTS}</h3>
      <h2 class="col20 f-left f-28 gray-text">
        <b>
          {parseNumber(stats.abandoned)}
          {#if stats.abandoned > 0}
            <p>({parsePercent(stats.abandoned / (stats.orders + stats.abandoned))})</p>
          {/if}
          {#if period && period.from}
            {#if stats.abandoned > stats.prevAbandoned}
              <span class="decrease">
                +{stats.prevAbandoned === 0 ? "100%" : parsePercent(stats.abandoned / stats.prevAbandoned - 1)}
                <span class="material-icons none"> arrow_downward </span>
              </span>
            {:else if stats.abandoned < stats.prevAbandoned}
              <span class="increase">
                {stats.prevAbandoned === 0 ? "-100%" : parsePercent(stats.abandoned / stats.prevAbandoned - 1)}
                <span class="material-icons none"> arrow_upward </span>
              </span>
            {:else}
              <span class="no-change">
                <span class="material-icons none"> remove </span>
              </span>
            {/if}
          {/if}
        </b>
      </h2>
    </div>
  </div>

  <!-- Top Analytics Part END -->

  <div class="col-twotirds f-left">
    <div class="flex f-left col20 mt-25 mb-25">
      <div class="analytics-box-big" style="padding-bottom: 0px;">
        <h2 class="col20 f-left f-28 gray-text">{M.SALES_REVENUE}</h2>

        <div class="col20 f-left relative mt-25 modern-chart-box" bind:this={chartWrapper}>
          <Chart data={chartData} options={chartOptions} />
          <div class="axis">
            {#each chartDataLabels as label, i}
              <div class="tick">
                <span class="tick-label">{label}</span>
                <span class="value value-first"><span class="chart-color" style="background: #3da6ff" />{chartValues[i].first}</span>
                <span class="value value-second"><span class="chart-color" style="background: #f97f7f" />{External.Tools.currencyToStrWithSymbol(chartValues[i].second)}</span>
              </div>
            {/each}
          </div>
          {#if grouping === "hourly"}
            <div class="col20 f-left flex flex-row chart-label-row">
              <span class="flex-1 ta-center"><p>AM</p></span>
              <span class="flex-1 ta-center"><p>PM</p></span>
            </div>
          {:else if grouping === "daily" && showMonthsBelow}
            <div class="col20 f-left flex flex-row chart-label-row">
              {#each monthLabels as label, i}
                <span
                  class="flex-1"
                  class:ta-left={i === 0 && monthLabels.length > 1}
                  class:ta-right={i === monthLabels.length - 1 && monthLabels.length > 1}
                  class:ta-center={monthLabels.length === 1 || (i > 1 && i < monthLabels.length - 1)}>
                  <p>{label}</p>
                </span>
              {/each}
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>

  {#if showReferers}
    <div class="one-third-analytics f-right mt-25">
      <div class="analytics-box">
        <div class="col20 flex align-center space-between">
          <h2 class="col20 f-left f-28 gray-text">{M.TOP_SOURCES}</h2>
          <a class="small-button blue circle-effect flex-none" on:click={showTopReferersBtnClick}>{M.VIEW_ALL}</a>
        </div>
        <div class="col20 f-left mt-10">
          <span class="light-gray-text f-left col20 f-left" style="font-size: 14px; line-height: 1.3;">
            {M.TOP_SOURCES_DESC}
          </span>
          <a on:click={generateUtmLink} class="small-button gray circle-effect flex-none mt-10 f-left ml-0">{M.GENERATE_LINK}</a>
        </div>
        <ul class="col20 f-left mt-25 ref-list">
          {#if stats.sources && stats.sources.length}
            {#each stats.sources as source}
              <li>
                <span
                  on:click={() => {
                    filterBySource(source.source);
                  }}>
                  {source.source}
                </span>
                <span class="revenue">{parseNumber(source.count)} - <b>{External.Tools.currencyToStrWithSymbol(source.total)}</b></span>
              </li>
            {/each}
          {/if}
        </ul>
        {#if !stats.sources || !stats.sources.length}
          <div class="col20 f-left mt-25 mb-25">
            <div class="col20 f-left mb-15 t-center">
              <span class="material-icons-outlined fingerprint-icon inline-block" style="background: #fff;"> bubble_chart </span>
            </div>
            <h4 class="col20 f-left t-center f-16">{M.NO_SOURCES_YET}</h4>
          </div>
        {/if}
      </div>
      <!-- Store Funnel Idea 
      <div class="analytics-box mt-15">
        <div class="col20 flex align-center space-between">
          <h2 class="col20 f-left f-28 gray-text">Store Main Funnel</h2>
        </div>
        <div class="col20 f-left mt-10">
          <span class="light-gray-text f-left col20 f-left" style="font-size: 14px; line-height: 1.3;">
            The main sales funnel of your storefront where customers pick a product and purchase it.
          </span>
        </div>
        <div class="col20 f-left mt-25 flex flex-col align-center">
          <div class="funnel-step-bar f-right" style="width: 100%;">
            Store Pages - 25
            <a class="step-conversion-badge button-tooltip" data-id="1" data-tooltip="stepConversion">64%</a>
            <a class="blue ab-split button-tooltip" data-tooltip="startABTest" data-original-title="" title="">A/B Test</a>
          </div>
          <div class="funnel-step-bar f-right mt-25" style="width: 64%;">
            Checkout - 18
            <a class="step-conversion-badge button-tooltip" data-id="1" data-tooltip="stepConversion">35%</a>
            <a class="blue ab-split button-tooltip" data-tooltip="startABTest" data-original-title="" title="">A/B Test</a>
          </div>
          <div class="funnel-step-bar f-right mt-25" style="width: 35%;">
            Thank You - 7
          </div>
        </div>
      </div>
       Store Funnel Idea END -->
    </div>
  {/if}
</div>

<style type="text/css">
  .no-referers .col-twotirds {
    width: 100%;
    margin-right: 0;
  }
</style>
